/*
 * Custom video player (Figma "Video player - states" 4032:1131). Drives
 * snippets/video-player.liquid - the uploaded-video path only. The
 * YouTube/Vimeo embed fallback (sections/video-steps.liquid) never renders
 * this snippet and is untouched by this file.
 *
 * States (mirrored on the root as [data-state]): idle, playing, paused,
 * scrubbing, ended. [data-controls-hidden] is a separate, independent flag -
 * only true while playing AND idle for HIDE_DELAY with no focus inside the
 * control bar (rule 3: the progress bar itself never hides, only the
 * buttons - see the CSS, which scopes the fade to .video-player__btn).
 */
(() => {
  const HIDE_DELAY = 2500;
  const SEEK_STEP = 5;
  const SPEEDS = [1, 1.5, 2];

  const players = document.querySelectorAll('[data-video-player]');
  if (!players.length) return;

  players.forEach(initPlayer);

  function initPlayer(root) {
    const video = root.querySelector('.video-player__video');
    const idle = root.querySelector('[data-video-player-idle]');
    const ended = root.querySelector('[data-video-player-ended]');
    const controls = root.querySelector('[data-video-player-controls]');
    const playCta = root.querySelector('[data-video-player-play-cta]');
    const replayCta = root.querySelector('[data-video-player-replay-cta]');
    const toggleBtn = root.querySelector('[data-video-player-toggle]');
    const muteBtn = root.querySelector('[data-video-player-mute]');
    const speedBtn = root.querySelector('[data-video-player-speed]');
    const fullscreenBtn = root.querySelector('[data-video-player-fullscreen]');
    const progress = root.querySelector('[data-video-player-progress]');
    const track = root.querySelector('[data-video-player-track]');
    const bufferedEl = root.querySelector('[data-video-player-buffered]');
    const playedEl = root.querySelector('[data-video-player-played]');
    const knob = root.querySelector('[data-video-player-knob]');
    const timeEl = root.querySelector('[data-video-player-time]');
    if (!video || !controls || !progress) return;

    const labels = root.dataset;
    const speedAriaTemplate = labels.speedAriaTemplate || 'Playback speed: {rate}';
    const timeTemplate = labels.timeTemplate || '{current} / {duration}';
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

    let speedIndex = 0;
    let hideTimer = null;
    let controlsFocused = false;
    let scrubbing = false;

    // ---- helpers ---------------------------------------------------

    function formatTime(seconds) {
      if (!isFinite(seconds) || seconds < 0) seconds = 0;
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return m + ':' + String(s).padStart(2, '0');
    }

    function setState(state) {
      root.dataset.state = state;
    }

    function scheduleHide() {
      clearTimeout(hideTimer);
      if (video.paused || controlsFocused) return;
      hideTimer = setTimeout(() => {
        if (!video.paused && !controlsFocused) root.setAttribute('data-controls-hidden', '');
      }, HIDE_DELAY);
    }

    function revealControls() {
      root.removeAttribute('data-controls-hidden');
      scheduleHide();
    }

    function controlsHidden() {
      return root.hasAttribute('data-controls-hidden');
    }

    // ---- play / pause / ended --------------------------------------

    function togglePlay() {
      if (video.paused || video.ended) {
        video.play();
      } else {
        video.pause();
      }
    }

    function syncToggleIcon() {
      const playing = !video.paused && !video.ended;
      toggleBtn.querySelector('[data-icon="play"]').hidden = playing;
      toggleBtn.querySelector('[data-icon="pause"]').hidden = !playing;
      toggleBtn.setAttribute('aria-label', playing ? labels.labelPause : labels.labelPlay);
    }

    playCta.addEventListener('click', () => {
      video.play();
    });

    replayCta.addEventListener('click', () => {
      video.currentTime = 0;
      video.play();
    });

    toggleBtn.addEventListener('click', () => {
      revealControls();
      togglePlay();
    });

    video.addEventListener('play', () => {
      idle.hidden = true;
      ended.hidden = true;
      controls.hidden = false;
      setState('playing');
      syncToggleIcon();
      revealControls();
    });

    video.addEventListener('pause', () => {
      if (video.ended) return;
      setState('paused');
      syncToggleIcon();
      root.removeAttribute('data-controls-hidden');
      clearTimeout(hideTimer);
    });

    video.addEventListener('ended', () => {
      setState('ended');
      controls.hidden = true;
      ended.hidden = false;
      root.removeAttribute('data-controls-hidden');
      clearTimeout(hideTimer);
    });

    video.addEventListener('click', () => {
      if (isCoarsePointer && controlsHidden()) {
        revealControls();
        return;
      }
      revealControls();
      togglePlay();
    });

    // ---- mute --------------------------------------------------------

    function syncMuteIcon() {
      muteBtn.querySelector('[data-icon="sound-on"]').hidden = video.muted;
      muteBtn.querySelector('[data-icon="sound-off"]').hidden = !video.muted;
      muteBtn.setAttribute('aria-pressed', String(video.muted));
      muteBtn.setAttribute('aria-label', video.muted ? labels.labelUnmute : labels.labelMute);
    }

    muteBtn.addEventListener('click', () => {
      revealControls();
      video.muted = !video.muted;
    });

    video.addEventListener('volumechange', syncMuteIcon);

    // ---- speed ---------------------------------------------------------

    function applySpeed() {
      const rate = SPEEDS[speedIndex];
      const label = rate + 'x';
      video.playbackRate = rate;
      speedBtn.textContent = label;
      speedBtn.setAttribute('aria-label', speedAriaTemplate.replace('{rate}', label));
    }

    speedBtn.addEventListener('click', () => {
      revealControls();
      speedIndex = (speedIndex + 1) % SPEEDS.length;
      applySpeed();
    });

    // ---- fullscreen ------------------------------------------------

    function isFullscreen() {
      return document.fullscreenElement === root || document.webkitFullscreenElement === root;
    }

    function syncFullscreenIcon() {
      const fs = isFullscreen();
      fullscreenBtn.querySelector('[data-icon="fullscreen"]').hidden = fs;
      fullscreenBtn.querySelector('[data-icon="fullscreen-exit"]').hidden = !fs;
      fullscreenBtn.setAttribute('aria-label', fs ? labels.labelFullscreenExit : labels.labelFullscreenEnter);
    }

    function toggleFullscreen() {
      if (!root.requestFullscreen && !root.webkitRequestFullscreen) {
        if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
        return;
      }
      if (!isFullscreen()) {
        (root.requestFullscreen || root.webkitRequestFullscreen).call(root);
      } else {
        (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      }
    }

    fullscreenBtn.addEventListener('click', () => {
      revealControls();
      toggleFullscreen();
    });

    document.addEventListener('fullscreenchange', syncFullscreenIcon);
    document.addEventListener('webkitfullscreenchange', syncFullscreenIcon);

    // ---- progress / seeking ------------------------------------------

    function percentFromEvent(e) {
      const rect = track.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return Math.min(1, Math.max(0, x / rect.width));
    }

    function paintProgress(percent, currentSeconds, durationSeconds) {
      const pct = percent * 100;
      playedEl.style.width = pct + '%';
      knob.style.left = pct + '%';
      timeEl.style.left = pct + '%';
      progress.setAttribute('aria-valuenow', String(Math.round(pct)));
      progress.setAttribute(
        'aria-valuetext',
        timeTemplate.replace('{current}', formatTime(currentSeconds)).replace('{duration}', formatTime(durationSeconds)),
      );
      timeEl.textContent = timeTemplate
        .replace('{current}', formatTime(currentSeconds))
        .replace('{duration}', formatTime(durationSeconds));
    }

    function syncProgress() {
      if (scrubbing) return;
      const duration = video.duration || 0;
      const percent = duration ? video.currentTime / duration : 0;
      paintProgress(percent, video.currentTime, duration);

      if (video.buffered && video.buffered.length && duration) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        bufferedEl.style.width = Math.min(100, (bufferedEnd / duration) * 100) + '%';
      }
    }

    video.addEventListener('timeupdate', syncProgress);
    video.addEventListener('durationchange', syncProgress);
    video.addEventListener('progress', syncProgress);

    function startScrub(e) {
      scrubbing = true;
      setState('scrubbing');
      timeEl.hidden = false;
      revealControls();
      seekToEvent(e);
      window.addEventListener('pointermove', seekToEvent);
      window.addEventListener('pointerup', endScrub, { once: true });
    }

    function seekToEvent(e) {
      const percent = percentFromEvent(e);
      const duration = video.duration || 0;
      paintProgress(percent, percent * duration, duration);
      video.currentTime = percent * duration;
    }

    function endScrub() {
      scrubbing = false;
      timeEl.hidden = true;
      setState(video.paused ? 'paused' : 'playing');
      window.removeEventListener('pointermove', seekToEvent);
      scheduleHide();
    }

    progress.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      startScrub(e);
    });

    progress.addEventListener('mouseenter', () => {
      if (!scrubbing) timeEl.hidden = false;
    });

    progress.addEventListener('mouseleave', () => {
      if (!scrubbing) timeEl.hidden = true;
    });

    progress.addEventListener('keydown', (e) => {
      const duration = video.duration || 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        video.currentTime = Math.min(duration, video.currentTime + SEEK_STEP);
        revealControls();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        video.currentTime = Math.max(0, video.currentTime - SEEK_STEP);
        revealControls();
      }
    });

    // ---- auto-hide: reveal on real pointer movement / touch / focus ----

    root.addEventListener('pointermove', revealControls);
    root.addEventListener('touchstart', revealControls, { passive: true });

    controls.addEventListener('focusin', () => {
      controlsFocused = true;
      revealControls();
    });

    controls.addEventListener('focusout', () => {
      controlsFocused = false;
      scheduleHide();
    });

    // ---- keyboard shortcuts: space/k play, m mute, f fullscreen --------

    root.addEventListener('keydown', (e) => {
      const key = e.key.toLowerCase();
      if ((key === ' ' || key === 'k') && e.target.tagName !== 'BUTTON') {
        e.preventDefault();
        revealControls();
        togglePlay();
      } else if (key === 'm') {
        revealControls();
        video.muted = !video.muted;
      } else if (key === 'f') {
        revealControls();
        toggleFullscreen();
      }
    });

    // ---- pause when scrolled out of view --------------------------------

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && !video.paused) video.pause();
          });
        },
        { threshold: 0 },
      );
      observer.observe(root);
    }

    // ---- init ------------------------------------------------------

    applySpeed();
    syncMuteIcon();
    syncToggleIcon();
  }
})();
